/// <reference lib="dom" />

import { useEffect, useRef, useState } from "react";
import { ChevronDown, ExternalLink, ThumbsDown, ThumbsUp } from "lucide-react";
import { id } from "@instantdb/react";
import type { EventEntry } from "../../../src/fields.ts";
import { api } from "../api.ts";
import { Spinner } from "./Spinner.tsx";
import { EventMap } from "./EventMap.tsx";
import { db } from "../db.ts";

type Country = "Israel" | "US" | "Germany" | "Argentina";

const countries: { value: Country | ""; label: string }[] = [
  { value: "", label: "All Countries" },
  { value: "Israel", label: "Israel" },
  { value: "US", label: "US" },
  { value: "Germany", label: "Germany" },
  { value: "Argentina", label: "Argentina" },
];

const monthNames = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const pad2 = (value: number) => String(value).padStart(2, "0");

const toQueryDatetime = (date: Date) =>
  `${monthNames[date.getMonth()]}-${pad2(date.getDate())} ${
    pad2(date.getHours())
  }:${pad2(date.getMinutes())}`;

const formatEventDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const EventRow = (
  { event, onSwipe }: {
    event: EventEntry;
    onSwipe: (direction: "like" | "dislike") => void;
  },
) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [feedback, setFeedback] = useState<"like" | "dislike" | null>(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const isSwipingRef = useRef<boolean | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    isSwipingRef.current = null;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (isSwipingRef.current === null) {
      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          isSwipingRef.current = true;
        } else {
          isSwipingRef.current = false;
        }
      }
    }

    if (isSwipingRef.current === true) {
      if (e.cancelable) {
        e.preventDefault();
      }
      setTranslateX(deltaX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (isSwipingRef.current === true) {
      const threshold = 120;
      if (translateX > threshold) {
        setTranslateX(globalThis.innerWidth);
        setTimeout(() => {
          onSwipe("like");
        }, 150);
      } else if (translateX < -threshold) {
        setTranslateX(-globalThis.innerWidth);
        setTimeout(() => {
          onSwipe("dislike");
        }, 150);
      } else {
        setTranslateX(0);
      }
    } else {
      setTranslateX(0);
    }
    isSwipingRef.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-lg mb-1 select-none">
      {/* Swipe Background Reveal */}
      {translateX !== 0 && (
        <div
          className={`absolute inset-0 flex items-center justify-between px-6 transition-colors duration-200 ${
            translateX > 0 ? "bg-green-500/20" : "bg-red-500/20"
          }`}
        >
          {/* Left side (Like, visible when swiping right) */}
          <div
            className={`flex items-center gap-2 text-green-400 font-semibold transition-opacity duration-150 ${
              translateX > 0 ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transform: `scale(${
                Math.min(1.2, Math.max(0.8, translateX / 100))
              })`,
            }}
          >
            <ThumbsUp size={24} />
            <span className="text-sm">Save</span>
          </div>

          {/* Right side (Dislike, visible when swiping left) */}
          <div
            className={`flex items-center gap-2 text-red-400 font-semibold transition-opacity duration-150 ${
              translateX < 0 ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transform: `scale(${
                Math.min(1.2, Math.max(0.8, -translateX / 100))
              })`,
            }}
          >
            <span className="text-sm">Pass</span>
            <ThumbsDown size={24} />
          </div>
        </div>
      )}

      {/* Actual Row Content */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: feedback === "like"
            ? "translateX(50px)"
            : feedback === "dislike"
            ? "translateX(-50px)"
            : `translateX(${translateX}px)`,
          opacity: feedback ? 0 : 1,
        }}
        className={`group border-b border-white/5 py-4 px-2 flex gap-4 items-start hover:bg-white/5 bg-[#1a1a1a]/40 rounded-lg relative transition-all duration-300 ease-in-out ${
          isDragging ? "transition-none" : ""
        } ${
          feedback === "like"
            ? "bg-green-500/20 border-green-500/30 scale-95"
            : feedback === "dislike"
            ? "bg-red-500/20 border-red-500/30 scale-95"
            : ""
        }`}
      >
        <div className="shrink-0 text-center w-14">
          <div className="text-xs text-gray-500 uppercase">
            {new Date(event.structured.timestamp.start).toLocaleDateString(
              "en-US",
              { month: "short" },
            )}
          </div>
          <div className="text-xl font-bold text-white">
            {new Date(event.structured.timestamp.start).getDate()}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-medium truncate">
              {event.structured.name}
            </h3>
            {event.recommendationScore !== undefined && (
              <span className="text-[10px] font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded-full shrink-0">
                ✨ {Math.round(event.recommendationScore * 100)}% Match
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mt-1">
            <span>{formatEventDate(event.structured.timestamp.start)}</span>
            {event.structured.venue && <span>{event.structured.venue}</span>}
            {event.structured.location && (
              <span>{event.structured.location}</span>
            )}
          </div>
          {event.structured.categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {event.structured.categories.slice(0, 4).map((cat) => (
                <span
                  key={cat}
                  className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30"
                >
                  {cat}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 self-center opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 focus-within:opacity-100 focus-within:scale-100 md:opacity-0 md:scale-95 transition-all duration-200">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFeedback("like");
              setTimeout(() => {
                onSwipe("like");
                setFeedback(null);
              }, 300);
            }}
            className="hidden md:flex px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 hover:border-green-500/40 text-green-400 hover:text-green-300 rounded-lg text-xs font-semibold tracking-wide transition-all active:scale-95 duration-150 items-center gap-1"
          >
            Save to My Events
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFeedback("dislike");
              setTimeout(() => {
                onSwipe("dislike");
                setFeedback(null);
              }, 300);
            }}
            className="hidden md:flex px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 text-red-400 hover:text-red-300 rounded-lg text-xs font-semibold tracking-wide transition-all active:scale-95 duration-150 items-center gap-1"
          >
            Pass
          </button>
          {event.structured.links.length > 0 && (
            <a
              href={event.structured.links[0]}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 hover:border-purple-500/40 text-purple-400 hover:text-purple-300 rounded-lg transition"
              title="Go to Link"
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const popularCategories = [
  "party",
  "techno",
  "trance",
  "house",
  "live music",
  "jazz",
  "standup",
  "talk",
  "film",
  "ecstatic",
  "sexuality",
  "sports",
  "family",
  "dance",
  "festival",
];

const getTimeRange = (option: string): { start: Date; end: Date } => {
  const now = new Date();

  if (option === "today") {
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
    );
    return { start: now, end };
  }

  if (option === "tomorrow") {
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0,
      0,
      0,
    );
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      23,
      59,
      59,
    );
    return { start, end };
  }

  if (option === "this-weekend") {
    const day = now.getDay();
    const fridayOffset = (5 - day + 7) % 7;
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + fridayOffset,
      0,
      0,
      0,
    );
    const sundayOffset = fridayOffset + 2;
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + sundayOffset,
      23,
      59,
      59,
    );

    if (day === 5 || day === 6 || day === 0) {
      const endOfWeekend = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + ((7 - day) % 7),
        23,
        59,
        59,
      );
      return { start: now, end: endOfWeekend };
    }

    return { start, end };
  }

  if (option === "this-week") {
    const day = now.getDay();
    const sundayOffset = (7 - day) % 7;
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + sundayOffset,
      23,
      59,
      59,
    );
    return { start: now, end };
  }

  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 30,
    23,
    59,
    59,
  );
  return { start: now, end };
};

export const EventList = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [country, setCountry] = useState<Country | "">("");
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mapRadius, setMapRadius] = useState<number>(20);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<string>("next-30-days");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [swipedIds, setSwipedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<"all" | "swiped">("all");
  const [recommendedEvents, setRecommendedEvents] = useState<EventEntry[]>([]);
  const [swipedActions, setSwipedActions] = useState<
    Map<string, "like" | "dislike">
  >(new Map());
  const [likedPastExpanded, setLikedPastExpanded] = useState(false);
  const [dislikedExpanded, setDislikedExpanded] = useState(false);
  const [lastSwipe, setLastSwipe] = useState<
    {
      eventId: string;
      eventName: string;
      direction: "like" | "dislike";
      swipeId: string;
    } | null
  >(null);
  const [showToast, setShowToast] = useState(false);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const user = db.useAuth().user;

  useEffect(() => {
    if (!user) return;
    api({
      endpoint: "getUserProfile",
      payload: {},
    })
      .then((profile) => {
        if (profile.coordinates) {
          setMapCenter(
            [profile.coordinates[0], profile.coordinates[1]] as [
              number,
              number,
            ],
          );
        }
        if (profile.country) {
          let mappedCountry = profile.country;
          if (profile.country === "USA") {
            mappedCountry = "US";
          }
          setCountry(mappedCountry as Country);
        }
      })
      .catch((err) => {
        console.error(
          "Failed to fetch user profile for default location:",
          err,
        );
      });
  }, [user]);
  const swipesQuery = db.useQuery(
    user
      ? {
        $users: {
          $: {
            where: { id: user.id },
          },
          swipes: {},
        },
      }
      : null,
  );

  const swipes = swipesQuery.data?.$users[0]?.swipes ?? [];
  const swipeMap = new Map(swipes.map((s) => [s.eventId, s.direction]));

  const getEventDirection = (
    eventId: string,
  ): "like" | "dislike" | undefined => {
    return swipeMap.get(eventId) ?? swipedActions.get(eventId);
  };

  const hasSwiped = (eventId: string): boolean => {
    return swipeMap.has(eventId) || swipedIds.has(eventId);
  };

  const visibleRecommended = recommendedEvents.filter((e) => !hasSwiped(e.id));

  const allSavedEvents = [...events];
  for (const e of recommendedEvents) {
    if (hasSwiped(e.id) && !allSavedEvents.some((x) => x.id === e.id)) {
      allSavedEvents.push(e);
    }
  }

  const likedFuture = viewMode === "swiped"
    ? allSavedEvents.filter((e) =>
      getEventDirection(e.id) === "like" &&
      e.structured.timestamp.start >= Date.now()
    )
    : [];

  const likedPast = viewMode === "swiped"
    ? allSavedEvents.filter((e) =>
      getEventDirection(e.id) === "like" &&
      e.structured.timestamp.start < Date.now()
    )
    : [];

  const disliked = viewMode === "swiped"
    ? allSavedEvents.filter((e) => getEventDirection(e.id) === "dislike")
    : [];

  const handleSwipe = (eventId: string, direction: "like" | "dislike") => {
    if (!user) return;
    const matchedEvent = events.find((e) => e.id === eventId) ??
      recommendedEvents.find((e) => e.id === eventId);
    const eventName = matchedEvent?.structured.name ?? "Event";

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setSwipedActions((prev) => {
      const next = new Map(prev);
      next.set(eventId, direction);
      return next;
    });
    setSwipedIds((prev) => {
      const next = new Set(prev);
      next.add(eventId);
      return next;
    });

    const swipeId = id();

    setLastSwipe({
      eventId,
      eventName,
      direction,
      swipeId,
    });
    setShowToast(true);

    db.transact(
      db.tx.swipes[swipeId].create({
        eventId,
        direction,
        timestamp: Date.now(),
      }).link({ user: user.id }),
    );

    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  const handleUndoSwipe = () => {
    if (!lastSwipe || !user) return;

    setSwipedIds((prev) => {
      const next = new Set(prev);
      next.delete(lastSwipe.eventId);
      return next;
    });
    setSwipedActions((prev) => {
      const next = new Map(prev);
      next.delete(lastSwipe.eventId);
      return next;
    });

    db.transact(db.tx.swipes[lastSwipe.swipeId].delete());

    setShowToast(false);
    setLastSwipe(null);
  };

  const visibleEvents = viewMode === "swiped"
    ? events
    : events.filter((e) => !hasSwiped(e.id));

  useEffect(() => {
    let active = true;

    setFetching(true);
    setError(null);

    if (viewMode === "swiped") {
      const start = new Date();
      const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);

      Promise.all([
        api({
          endpoint: "getUserSavedEvents",
          payload: {
            userId: user?.id,
          },
        }),
        api({
          endpoint: "getEvents",
          payload: {
            time: {
              startDatetime: toQueryDatetime(start),
              endDatetime: toQueryDatetime(end),
            },
            onlyStartedInRange: true,
            ...(country && !mapCenter
              ? {
                country: (country === "US" ? "USA" : country) as
                  | "Israel"
                  | "Germany"
                  | "USA",
              }
              : {}),
            ...(mapCenter
              ? { coordinates: mapCenter, radiusKm: mapRadius }
              : {}),
            userId: user?.id,
          },
        }),
      ])
        .then(([swipedRes, unswipedRes]) => {
          if (!active) return;
          setEvents(swipedRes);

          const filteredRecommended = unswipedRes.filter(
            (e) =>
              e.recommendationScore !== undefined &&
              e.recommendationScore >= 0.9,
          ).sort((a, b) => b.recommendationScore! - a.recommendationScore!);

          setRecommendedEvents(filteredRecommended);
        })
        .catch((err: unknown) => {
          if (!active) return;
          setError(
            err instanceof Error ? err.message : "Failed to load events",
          );
        })
        .finally(() => {
          if (active) setFetching(false);
        });
      return () => {
        active = false;
      };
    }

    const { start, end } = getTimeRange(timeFilter);

    api({
      endpoint: "getEvents",
      payload: {
        time: {
          startDatetime: toQueryDatetime(start),
          endDatetime: toQueryDatetime(end),
        },
        onlyStartedInRange: true,
        ...(country && !mapCenter
          ? {
            country: (country === "US" ? "USA" : country) as
              | "Israel"
              | "Germany"
              | "USA",
          }
          : {}),
        ...(mapCenter ? { coordinates: mapCenter, radiusKm: mapRadius } : {}),
        ...(categoryFilter ? { categories: [categoryFilter] } : {}),
        userId: user?.id,
      },
    })
      .then((res) => {
        if (!active) return;
        const sorted = res.slice().sort((a, b) => {
          return a.structured.timestamp.start - b.structured.timestamp.start;
        });
        setEvents(sorted);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Failed to load events");
      })
      .finally(() => {
        if (active) setFetching(false);
      });

    return () => {
      active = false;
    };
  }, [
    viewMode,
    country,
    mapCenter,
    mapRadius,
    showMap,
    timeFilter,
    categoryFilter,
    user?.id,
  ]);

  return (
    <div className="container mx-auto max-w-2xl px-4 py-6">
      {/* View Mode Toggle */}
      <div className="flex gap-2 p-1 bg-white/5 border border-white/10 rounded-xl mb-6 max-w-xs">
        <button
          type="button"
          onClick={() => setViewMode("all")}
          className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${
            viewMode === "all"
              ? "bg-purple-600 text-white shadow-md shadow-purple-500/10"
              : "text-gray-400 hover:text-white"
          }`}
        >
          All Events
        </button>
        <button
          type="button"
          onClick={() => setViewMode("swiped")}
          className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${
            viewMode === "swiped"
              ? "bg-purple-600 text-white shadow-md shadow-purple-500/10"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Your Events
        </button>
      </div>

      {/* Controls */}
      {viewMode === "all" && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-6 bg-white/5 border border-white/10 rounded-2xl p-4">
          {/* Country Filter */}
          {!showMap && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="country-filter"
                className="text-gray-400 text-xs uppercase font-semibold tracking-wider"
              >
                Country
              </label>
              <div className="relative flex items-center">
                <select
                  id="country-filter"
                  value={country}
                  onChange={(e) => {
                    const val = e.currentTarget.value;
                    setCountry(val as Country | "");
                  }}
                  className="appearance-none bg-[#1a1a1a] border border-white/10 rounded-lg pl-3 pr-10 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 transition cursor-pointer"
                >
                  {countries.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Time Filter */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="time-filter"
              className="text-gray-400 text-xs uppercase font-semibold tracking-wider"
            >
              When
            </label>
            <div className="relative flex items-center">
              <select
                id="time-filter"
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.currentTarget.value)}
                className="appearance-none bg-[#1a1a1a] border border-white/10 rounded-lg pl-3 pr-10 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 transition cursor-pointer"
              >
                <option value="next-30-days">Next 30 Days</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="this-week">This Week</option>
                <option value="this-weekend">This Weekend</option>
              </select>
              <ChevronDown className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="category-filter"
              className="text-gray-400 text-xs uppercase font-semibold tracking-wider"
            >
              Category
            </label>
            <div className="relative flex items-center">
              <select
                id="category-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.currentTarget.value)}
                className="appearance-none bg-[#1a1a1a] border border-white/10 rounded-lg pl-3 pr-10 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 transition cursor-pointer max-w-[150px]"
              >
                <option value="">All Categories</option>
                {popularCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Toggle Map Button */}
          <button
            type="button"
            onClick={() => {
              setShowMap(!showMap);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
              showMap
                ? "bg-purple-600/20 text-purple-300 border-purple-500/30 shadow-lg shadow-purple-500/5"
                : "text-gray-400 hover:text-white hover:bg-white/5 border-white/10"
            }`}
          >
            🗺️ {showMap ? "Hide Map" : "Show Map"}
          </button>

          <p
            className={`text-gray-500 text-sm ml-auto font-medium transition-opacity duration-150 ${
              fetching ? "opacity-0" : "opacity-100"
            }`}
          >
            {visibleEvents.length} events
          </p>
        </div>
      )}

      {viewMode === "all" && mapCenter && (
        <div className="flex items-center justify-between bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 mb-6 text-sm text-purple-200">
          <div className="flex items-center gap-2">
            <span>📍</span>
            <span>
              Filtering within <strong>{Math.round(mapRadius)}km</strong>{" "}
              of map location (
              {mapCenter[0].toFixed(3)}, {mapCenter[1].toFixed(3)})
            </span>
          </div>
          <button
            type="button"
            onClick={() => setMapCenter(null)}
            className="text-xs font-semibold px-2.5 py-1 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg border border-purple-500/30 transition text-purple-300"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Collapsible Interactive Map */}
      {viewMode === "all" && showMap && (
        <div className="mb-6">
          <EventMap
            events={visibleEvents}
            country={country}
            center={mapCenter}
            onBoundsChange={(coords, radius) => {
              setMapCenter(coords);
              setMapRadius(radius);
            }}
          />
        </div>
      )}

      {/* Events List / Loading / Error Container */}
      <div className="relative min-h-[200px]">
        {error
          ? <div className="text-red-400 text-center py-12">{error}</div>
          : fetching && (viewMode === "swiped"
              ? (visibleEvents.length === 0 && visibleRecommended.length === 0)
              : visibleEvents.length === 0)
          ? (
            <div className="flex justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          )
          : (viewMode === "swiped"
              ? (visibleEvents.length === 0 && visibleRecommended.length === 0)
              : visibleEvents.length === 0)
          ? (
            <div className="text-gray-400 text-center py-12">
              {viewMode === "swiped"
                ? "You haven't saved any events yet."
                : "No events found in this area."}
            </div>
          )
          : viewMode === "swiped"
          ? (
            <div
              className={fetching
                ? "opacity-40 transition-opacity space-y-8"
                : "transition-opacity space-y-8"}
            >
              {visibleRecommended.length > 0 && (
                <div>
                  <h2 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2 tracking-wide uppercase">
                    ✨ Undecided + Recommended ({visibleRecommended.length})
                  </h2>
                  <div className="space-y-1">
                    {visibleRecommended.map((event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onSwipe={(dir) => handleSwipe(event.id, dir)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {likedFuture.length > 0 && (
                <div
                  className={visibleRecommended.length > 0
                    ? "border-t border-white/5 pt-4"
                    : ""}
                >
                  <h2 className="text-sm font-bold text-green-400/90 mb-3 flex items-center gap-2 tracking-wide uppercase">
                    💚 Saved (Upcoming) ({likedFuture.length})
                  </h2>
                  <div className="space-y-1">
                    {likedFuture.map((event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onSwipe={(dir) => handleSwipe(event.id, dir)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {likedPast.length > 0 && (
                <div className="border-t border-white/5 pt-4">
                  <button
                    type="button"
                    onClick={() => setLikedPastExpanded(!likedPastExpanded)}
                    className="w-full flex items-center justify-between text-left text-sm font-bold text-gray-400/90 mb-3 tracking-wide uppercase group hover:text-white transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      ⏳ Saved (Past) ({likedPast.length})
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-gray-500 group-hover:text-white transition-transform duration-200 ${
                        likedPastExpanded ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                  {likedPastExpanded && (
                    <div className="space-y-1">
                      {likedPast.map((event) => (
                        <EventRow
                          key={event.id}
                          event={event}
                          onSwipe={(dir) => handleSwipe(event.id, dir)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {disliked.length > 0 && (
                <div className="border-t border-white/5 pt-4">
                  <button
                    type="button"
                    onClick={() => setDislikedExpanded(!dislikedExpanded)}
                    className="w-full flex items-center justify-between text-left text-sm font-bold text-red-400/80 mb-3 tracking-wide uppercase group hover:text-red-300 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      💔 Passed ({disliked.length})
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-red-500/50 group-hover:text-red-300 transition-transform duration-200 ${
                        dislikedExpanded ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                  {dislikedExpanded && (
                    <div className="space-y-1">
                      {disliked.map((event) => (
                        <EventRow
                          key={event.id}
                          event={event}
                          onSwipe={(dir) => handleSwipe(event.id, dir)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
          : (
            <div
              className={fetching
                ? "opacity-40 transition-opacity"
                : "transition-opacity"}
            >
              {visibleEvents.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  onSwipe={(dir) => handleSwipe(event.id, dir)}
                />
              ))}
            </div>
          )}

        {fetching && (viewMode === "swiped"
          ? (visibleEvents.length > 0 || visibleRecommended.length > 0)
          : visibleEvents.length > 0) &&
          (
            <div className="absolute inset-0 flex items-center justify-center z-[999]">
              <Spinner className="h-8 w-8 text-purple-500" />
            </div>
          )}
      </div>

      {/* Toast Notification for Undo */}
      {showToast && lastSwipe && (
        <div className="fixed bottom-6 right-6 z-[9999] bg-slate-900 border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-fade-in duration-300">
          <span className="text-sm font-medium">
            {lastSwipe.direction === "like"
              ? (
                <>
                  Saved "{lastSwipe.eventName}" to{" "}
                  <span className="text-green-400 font-semibold">
                    Your Events
                  </span>
                </>
              )
              : (
                <>
                  Passed on "{lastSwipe.eventName}"
                </>
              )}
          </span>
          <button
            type="button"
            onClick={handleUndoSwipe}
            className="ml-2 text-xs bg-purple-600 hover:bg-purple-500 text-white font-bold px-3 py-1.5 rounded-lg transition-colors active:scale-95"
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
};
